/**
 * NewConversationLauncher (INBOX-COMPOSE-01, Seth 2026-09-02: "Can't start a
 * conversation or create a new conversation inside of inbox").
 *
 * The Inbox was receive-and-reply only — a fresh outbound thread could only
 * be started from the customer's 360. This is the "+" in the Inbox header:
 * pick the customer (search anything), choose Text or Email, and the same
 * consent-aware composers the 360 uses take it from there. When the message
 * goes out, the conversation is claimed as yours and opened on the right,
 * so the thread you just started is the one you're looking at.
 *
 * While comms are in testing the composer holds the message exactly as it
 * does on the 360 — nothing reaches a customer until go-live.
 */
import { useState } from "react";
import { Plus, MessageSquare, Mail, ArrowLeft } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { CustomerPicker, type PickedCustomer } from "@/components/CustomerPicker";
import { ComposeTextModal, ComposeEmailModal } from "@/features/customer-360/CustomerMessages";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { cn } from "@/lib/cn";

export function NewConversationLauncher({ onStarted }: {
  /** Called after a message actually went out (or was held by the gate) for this customer. */
  onStarted: (custno: string) => void;
}) {
  const canSend = useCan("comms.send");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<PickedCustomer | null>(null);
  const [channel, setChannel] = useState<"sms" | "email" | null>(null);

  if (!canSend) return null;

  const reset = () => { setOpen(false); setPicked(null); setChannel(null); };
  const started = () => { const c = picked?.custno; reset(); if (c) onStarted(c); };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="New Conversation — text or email a customer"
        aria-label="New Conversation"
        className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-brand-700 px-3 text-[12.5px] font-bold text-white shadow-sm transition-colors hover:bg-brand-800"
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} /> New
      </button>

      <Modal
        open={open && !channel}
        onClose={reset}
        title="New Conversation"
        subtitle={picked ? `To ${picked.name}` : "Find the customer, then pick how to reach them."}
        size="md"
      >
        <div className="space-y-4">
          {!picked ? (
            // min-height: the picker's results drop down inside the modal's
            // scroll area, so give them room instead of a clipped list.
            <div className="min-h-[20rem]">
              <CustomerPicker autoFocus onSelect={setPicked} placeholder="Search name, address, phone, or customer #…" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 rounded-xl border border-hairline bg-slate-50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{picked.name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {[picked.address, picked.city].filter(Boolean).join(", ") || `Customer #${picked.custno}`}
                  </p>
                </div>
                <button type="button" onClick={() => setPicked(null)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
                  <ArrowLeft className="h-3.5 w-3.5" /> Change
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ChannelButton Icon={MessageSquare} label="Text" hint="Podium text to a number on file" tone="emerald" onClick={() => setChannel("sms")} />
                <ChannelButton Icon={Mail} label="Email" hint="From service@ with a subject line" tone="sky" onClick={() => setChannel("email")} />
              </div>
              <p className="text-[11px] text-slate-400">Consent rules apply exactly as on the customer&apos;s page: STOP numbers and unsubscribed emails stay blocked.</p>
            </>
          )}
        </div>
      </Modal>

      {picked && channel === "sms" && (
        <ComposeTextModal custno={picked.custno} onClose={() => setChannel(null)} onSent={started} />
      )}
      {picked && channel === "email" && (
        <ComposeEmailModal custno={picked.custno} onClose={() => setChannel(null)} onSent={started} />
      )}
    </>
  );
}

function ChannelButton({ Icon, label, hint, tone, onClick }: {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  tone: "emerald" | "sky";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors",
        tone === "emerald" ? "border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50" : "border-sky-200 bg-sky-50/60 hover:bg-sky-50",
      )}
    >
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full", tone === "emerald" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700")}>
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-slate-900">{label}</span>
        <span className="block text-xs text-slate-500">{hint}</span>
      </span>
    </button>
  );
}
